import React, { ChangeEvent } from "react";
import { RouteComponentPropsHistory } from "../../types";

import withAuth from "../withAuth";
import withTitle from "../withTitle";

import DelayInput from "../DelayInput";
import { IAuthProps, WellKnownGroups } from "../AuthContext";

import { V2 } from "../../Api";
import { formatDateTimeLocal } from "../../Utilities";

import "../../table.css";


interface ITelemedFormListViewState {
    isLoading: boolean | string;
    newForms: any[];
    oldForms: any[];
    locations: any[];

    searchOffsetNew: number;
    searchOffsetOld: number;
    searchText: string;
    searchLocation: string;
}

const PAGE_SIZE = 30;

class TelemedFormListView extends React.Component<RouteComponentPropsHistory<any, any> & IAuthProps, ITelemedFormListViewState> {
    private _refreshTimer?: number;

    constructor(props: Readonly<RouteComponentPropsHistory<any, any> & IAuthProps>) {
        super(props);

        const state: ITelemedFormListViewState = {
            isLoading: true,
            newForms: [],
            oldForms: [],
            locations: [],

            searchOffsetNew: 0,
            searchOffsetOld: 0,
            searchText: "",
            searchLocation: props.authContext.isInGroup(WellKnownGroups.FrontDesk)
                ? props.authContext.user.employee.locationId
                : "",
        };

        if (props.location.state) {
            const prevState = props.location.state;
            state.searchOffsetNew = prevState.searchOffsetNew || 0;
            state.searchOffsetOld = prevState.searchOffsetOld || 0;
            state.searchText = prevState.searchText || "";
            state.searchLocation = prevState.searchLocation || "";
        }

        this.state = state;

        this.handleElementChange = this.handleElementChange.bind(this);
        this.search = this.search.bind(this);
        this.previousPage = this.previousPage.bind(this);
        this.nextPage = this.nextPage.bind(this);
    }

    public async componentDidMount() {
        try {
            const [locations] = await Promise.all([
                V2.get<any[]>("locations?type=office")
            ]);

            locations.sort((a, b) => (a.shortName || a.name).localeCompare(b.shortName || b.name));

            this.setState({
                locations
            }, this.search);

            this._refreshTimer = window.setInterval(() => {
                this.search(true);
            }, 30 * 1000)
        }
        catch (e) {
            this.setState({
                isLoading: e.message || "Could not load page data"
            });
        }
    }

    public componentWillUnmount() {
        if (this._refreshTimer) {
            window.clearInterval(this._refreshTimer);
            this._refreshTimer = undefined;
        }
    }

    public render() {
        return (
            <div className="viewer-root">
                <div className="filter-control-box">
                    <DelayInput timeout={300} className="fill-space" type="search" placeholder="Search..." name="searchText" value={this.state.searchText} onChangeDelayed={this.handleElementChange} />
                    <select value={this.state.searchLocation} name="searchLocation" onChange={this.handleElementChange} disabled={this.state.isLoading === true || !!this.state.searchText}>
                        <option value="">All Locations</option>
                        {this.state.locations.map(a => <option key={a.id} value={a.id}>{a.shortName || a.name}</option>)}
                    </select>
                </div>
                <h3>New</h3>
                {this.renderTable(this.state.newForms, this.state.searchOffsetNew, "new")}

                <h3>Everything Else</h3>
                {this.renderTable(this.state.oldForms, this.state.searchOffsetOld, "old")}
            </div>
        );
    }

    private renderTable(forms: any[], offset: number, kind: "new" | "old") {
        let table;

        const buttons =
            <div className="action-panel-dialog action-panel-noflip">
                {offset > 0 ? <button className="button push-right text-with-icon" onClick={() => this.previousPage(kind)}><i className="icon-angle-left" /><span>Newer</span></button> : null}
                <button className={"button text-with-icon" + (offset === 0 ? " push-right" : "")} onClick={() => this.nextPage(kind)} disabled={forms.length < PAGE_SIZE}><span>Older</span><i className="icon-angle-right" /></button>
            </div>;

        if (typeof this.state.isLoading === "string") {
            table = <span className="error-text">{this.state.isLoading}</span>;
        }
        else if (forms.length === 0 && offset === 0) {
            table = <>
                <span>No forms found</span>
            </>;
        }
        else {
            table = <>
                <table className="table-vertical table-clickable">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Office</th>
                            <th className="col-smallest">Date Submitted</th>
                        </tr>
                    </thead>
                    <thead className="table-vertical-head-small">
                        <tr>
                            <th>Forms</th>
                        </tr>
                    </thead>
                    <tbody>
                        {this.state.isLoading === true
                            ? <tr>
                                <td colSpan={10}>
                                    <div className="spinner-center lds-dual-ring-small" />
                                </td>
                            </tr>
                            : forms.length === 0
                                ? <tr>
                                    <td colSpan={10}>No more forms</td>
                                </tr>
                                : forms.map(a => <tr key={a.id} className="clickable" role="link" tabIndex={0} onClick={() => this.goForm(a.id)}>
                                    <td data-caption="Name: ">{a.name}</td>
                                    <td data-caption="Office: ">{a.locationName}</td>
                                    <td data-caption="Date submitted: ">{formatDateTimeLocal(a.timestamp)}</td>
                                </tr>)
                        }
                    </tbody>
                </table>
                &nbsp;
                {buttons}
            </>;
        }

        return table;
    }

    private async search(soft?: boolean) {
        try {
            if (soft !== true) {
                this.setState({
                    isLoading: true
                });
            }

            this.saveSearchState();

            const parameters = [
                `limit=${PAGE_SIZE}`,
            ];
            const parametersNew = ["status=new"];
            const parametersOld = ["status=read"];
            if (this.state.searchText) {
                parameters.push(`q=${this.state.searchText}`);
            }
            else {
                // Ignore these filters when searching
                if (this.state.searchLocation) {
                    parameters.push(`location=${this.state.searchLocation}`);
                }

                parametersNew.push(`offset=${this.state.searchOffsetNew}`);
                parametersOld.push(`offset=${this.state.searchOffsetOld}`);
            }

            Array.prototype.push.apply(parametersNew, parameters);
            Array.prototype.push.apply(parametersOld, parameters);

            const [newForms, oldForms] = await Promise.all([
                V2.get<any[]>(`forms?${parametersNew.join("&")}`),
                V2.get<any[]>(`forms?${parametersOld.join("&")}`),
            ]);

            this.setState({
                isLoading: false,
                newForms,
                oldForms,
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message || "Could not load forms"
            });
        }
    }

    private handleElementChange(e: ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLInputElement>) {
        const value = e.currentTarget.value;
        const name = e.currentTarget.name;

        this.setState({
            searchOffset: 0,

            [name]: value
        } as any, () => this.search());
    }

    private saveSearchState() {
        const location = this.props.location;
        location.state = {
            searchOffsetNew: this.state.searchOffsetNew,
            searchOffsetOld: this.state.searchOffsetOld,
            searchText: this.state.searchText,
            searchLocation: this.state.searchLocation,
        };

        this.props.history.replace(location);
    }

    private nextPage(kind: "new" | "old") {
        this.setState(s => kind === "new"
            ? { searchOffsetNew: s.searchOffsetNew + PAGE_SIZE } as any
            : { searchOffsetOld: s.searchOffsetOld + PAGE_SIZE } as any,
            () => this.search());
    }

    private previousPage(kind: "new" | "old") {
        this.setState(s => kind === "new"
            ? { searchOffsetNew: Math.max(s.searchOffsetNew - PAGE_SIZE, 0) } as any
            : { searchOffsetOld: Math.max(s.searchOffsetOld - PAGE_SIZE, 0) } as any,
            () => this.search());
    }

    private goForm(id: string) {
        this.props.history.push(`${this.props.match.url}/${id}`);
    }
}

export default withTitle("Received Telemed Forms", withAuth(TelemedFormListView));